import { useEffect, useRef, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { auth0Config, setAuthRefs } from '@/utils/auth';
import { eventBus, EventTypes } from '@/lib/custom-event-bus';
import { AUTH_REDIRECT_URL } from '@/utils/local_storage_keys';
import { isLovablePreview } from '@/lib/utils/environment';
import { apolloClient } from '@/lib/graphql/apollo';

// ── Public routes that skip auth checks ───────────────────────────────────────

// '/work-email-required' is public: the visitor holds a valid Auth0 session but the
// backend refused to provision a workspace for their free email, so an auth check here
// would bounce them straight back into the loop they were just ejected from.
const PUBLIC_PREFIXES = ['/callback', '/login', '/shared/', '/work-email-required'];

function isPublicRoute(pathname: string) {
  return PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
}

// Where MCP servers send an admin back after authorizing; see McpCallbackPage.
const MCP_CALLBACK_PATH = '/integrations/mcp/callback';

export function isMcpCallbackPath(pathname: string) {
  return pathname === MCP_CALLBACK_PATH || pathname === `${MCP_CALLBACK_PATH}/`;
}

// ── Inner provider (must be inside Auth0Provider + BrowserRouter) ─────────────

function AuthGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const {
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    logout,
    loginWithRedirect,
  } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();

  // Expose SDK functions to non-React code (Apollo, Axios). Register
  // synchronously during render so the very first GraphQL query — which Apollo
  // fires before any effect has run — can obtain a token. Without this, the
  // first post-auth render lands on /onboarding with no user data and stays
  // blank until the user refreshes. Keep an effect as a safety net for the
  // edge case where the SDK functions change identity.
  const lastRegistered = useRef<{ get: unknown; logout: unknown } | null>(null);
  if (
    lastRegistered.current?.get !== getAccessTokenSilently ||
    lastRegistered.current?.logout !== logout
  ) {
    lastRegistered.current = { get: getAccessTokenSilently, logout };
    setAuthRefs(
      getAccessTokenSilently as (options?: any) => Promise<string>,
      logout,
    );
  }

  // Route-change auth check — redirect to login if not authenticated
  useEffect(() => {
    if (isLovablePreview()) return;
    if (isLoading) return;
    if (isPublicRoute(location.pathname)) return;

    if (!isAuthenticated) {
      localStorage.setItem(AUTH_REDIRECT_URL, location.pathname);
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  // Proactive token refresh on tab visibility change (laptop sleep/wake)
  useEffect(() => {
    if (isLovablePreview()) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        // SDK will only refresh if needed (token expired or close to expiry)
        getAccessTokenSilently().catch((err) => {
          console.warn('auth: visibility refresh failed:', err);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, getAccessTokenSilently]);

  // Cross-tab logout sync
  const handleCrossTabLogout = useCallback(() => {
    console.log('Logout detected from another tab');
    apolloClient.clearStore();
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    const unsubscribe = eventBus.on(EventTypes.LOGOUT, handleCrossTabLogout);
    return () => { unsubscribe(); };
  }, [handleCrossTabLogout]);

  if (isLoading && !isLovablePreview()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

// ── Outer wrapper that sets up Auth0Provider ──────────────────────────────────

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  if (isLovablePreview()) {
    // In preview mode, skip Auth0 entirely
    return <>{children}</>;
  }

  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={auth0Config.authorizationParams}
      useRefreshTokens={auth0Config.useRefreshTokens}
      cacheLocation={auth0Config.cacheLocation}
      useRefreshTokensFallback={auth0Config.useRefreshTokensFallback}
      // Auth0 treats any URL with ?code=&state= as its own login callback and strips them. An
      // MCP server's OAuth callback carries the same params for McpCallbackPage, so leave it be.
      skipRedirectCallback={isMcpCallbackPath(window.location.pathname)}
    >
      <AuthGate>{children}</AuthGate>
    </Auth0Provider>
  );
}
